// The fetch wrapper is the one place that disambiguates *why* a request
// failed, and it keeps the two failure modes cleanly separate (the v2.4
// "error disambiguation" requirement):
//
//   • 401 (unauthenticated) → the session is gone. App.jsx's handler snapshots
//     any open draft and shows the graceful "session timed out" modal instead
//     of leaving the user with a confusing "nothing imported from Jira".
//   • a genuine connectivity failure (fetch throws: offline, DNS, timeout) or
//     an unexpected 5xx → the server/network is unreachable, NOT a sign-out.
//     App.jsx's handler shows the "can't reach ClockIn — your session is still
//     active" modal.
//
// 403 is deliberately NOT routed to either: in this app it means "your role
// isn't allowed here" (e.g. a plain employee hitting /api/team), which the
// callers already treat as an empty result — never a session event.
//
// 503 is also left to its callers: it's the write-queue's "server busy, try
// again" signal with dedicated inline retry UX on the Log Time / import flows,
// so it must not pop the global network modal on top of that.
let unauthorizedHandler = null;
let networkErrorHandler = null;
let sessionTtlListener = null;

export function setUnauthorizedHandler(fn) {
  unauthorizedHandler = typeof fn === 'function' ? fn : null;
}

// Notified on connectivity loss / unexpected 5xx (NOT 401, NOT 403, NOT 503).
export function setNetworkErrorHandler(fn) {
  networkErrorHandler = typeof fn === 'function' ? fn : null;
}

// Notified with the remaining session TTL (seconds) that the server stamps on
// every authenticated response — drives the proactive countdown without any
// extra polling. App.jsx ticks it down client-side between requests.
export function setSessionTtlListener(fn) {
  sessionTtlListener = typeof fn === 'function' ? fn : null;
}

// Fire a handler detached, swallowing sync throws and async rejections, so a
// misbehaving handler never masks the original error or surfaces unhandled.
function fireDetached(handler, arg) {
  if (!handler) return;
  void Promise.resolve()
    .then(() => handler(arg))
    .catch(() => {});
}

function readTtlHeader(res) {
  if (!sessionTtlListener) return;
  let raw;
  try {
    raw = res.headers?.get?.('X-Clockin-Ttl');
  } catch {
    return;
  }
  if (raw == null) return;
  const secs = parseInt(raw, 10);
  if (Number.isFinite(secs)) fireDetached(sessionTtlListener, secs);
}

async function request(method, path, body) {
  let res;
  try {
    res = await fetch(path, {
      method,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (cause) {
    // fetch only rejects on a genuine connectivity failure (offline, DNS,
    // CORS-blocked, abort) — never on an HTTP error status. This is a network
    // problem, not a sign-out.
    const err = new Error('network_error');
    err.code = 'NETWORK';
    err.cause = cause;
    fireDetached(networkErrorHandler, err);
    throw err;
  }

  // Keep the countdown in sync off every response (incl. error responses that
  // still carry the header), so an active user never sees a stale warning.
  readTtlHeader(res);

  if (res.status === 401) {
    const err = new Error('unauthenticated');
    err.code = 'UNAUTH';
    fireDetached(unauthorizedHandler, err);
    throw err;
  }

  let data = null;
  try {
    data = await res.json();
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    // Unexpected server failure → network modal. 503 is the write-queue's
    // intentional "busy, retry" signal (handled inline by the callers), so it
    // keeps its own UX and never reaches the global handler.
    if (res.status >= 500 && res.status !== 503) {
      err.code = 'NETWORK';
      fireDetached(networkErrorHandler, err);
    }
    throw err;
  }
  return data;
}

export const api = {
  me: () => request('GET', '/api/me'),
  // v2.4 — remaining session TTL in seconds. Hitting it also slides the
  // inactivity window server-side, so it doubles as the "Stay signed in"
  // keep-alive. The countdown otherwise rides the X-Clockin-Ttl header on
  // every other call, so this is only invoked on an explicit keep-alive.
  sessionTtl: () => request('GET', '/api/session/ttl'),
  dictionary: () => request('GET', '/api/dictionary'),
  entries: (date) =>
    request('GET', `/api/entries?date=${encodeURIComponent(date)}`),
  entriesRange: (from, to) =>
    request(
      'GET',
      `/api/entries?from=${encodeURIComponent(
        from,
      )}&to=${encodeURIComponent(to)}`,
    ),
  entriesSummary: (from, to) =>
    request(
      'GET',
      `/api/entries/summary?from=${encodeURIComponent(
        from,
      )}&to=${encodeURIComponent(to)}`,
    ),
  createEntry: (entry) => request('POST', '/api/entries', entry),
  updateEntry: (id, entry) => request('PUT', `/api/entries/${id}`, entry),
  deleteEntry: (id) => request('DELETE', `/api/entries/${id}`),
  searchEpics: (q) =>
    request('GET', `/api/jira/epics?q=${encodeURIComponent(q || '')}`),
  searchJira: (q) =>
    request('GET', `/api/jira/search?q=${encodeURIComponent(q || '')}`),
  jiraActivity: (date) =>
    request('GET', `/api/jira/activity?date=${encodeURIComponent(date)}`),
  logout: () => request('POST', '/auth/logout'),
  mockLogin: (email) => request('POST', '/auth/mock-login', { email }),
  // Calendar — per-user OAuth. status returns { configured, connected }.
  // connect navigates the whole window to Google's consent screen, so
  // it's not part of the JSON api object.
  calendarStatus: () => request('GET', '/auth/calendar/status'),
  calendarDisconnect: () => request('POST', '/auth/calendar/disconnect'),
  calendarEvents: (date) =>
    request('GET', `/api/calendar/events?date=${encodeURIComponent(date)}`),
  calendarIgnore: (eventId, seriesId, title) =>
    request('POST', '/api/calendar/ignores', { eventId, seriesId, title }),
  calendarIgnoreList: () => request('GET', '/api/calendar/ignores'),
  calendarRestore: (eventId) =>
    request('DELETE', `/api/calendar/ignores/${encodeURIComponent(eventId)}`),
  // v2.0 — country-scoped holidays. With no args, returns the year for
  // the authenticated user's team. Used by Today's date bar (flag chips
  // + holiday empty state) and Summary's working-day math.
  // v2.0 — monthly target override fetch for Summary's hero math.
  monthlyTarget: (month, country) => {
    const params = new URLSearchParams({ month });
    if (country) params.set('country', country);
    return request('GET', `/api/monthly-target?${params.toString()}`);
  },
  holidays: (opts = {}) => {
    const params = new URLSearchParams();
    if (opts.from) params.set('from', opts.from);
    if (opts.to) params.set('to', opts.to);
    if (opts.country) params.set('country', opts.country);
    const q = params.toString();
    return request('GET', `/api/holidays${q ? '?' + q : ''}`);
  },
  // v2.0 (A4) — approver / Team view surfaces. 403 on these means the
  // caller's role isn't an approver; the UI hides them in that case.
  teamReports: () => request('GET', '/api/team/reports'),
  teamMonth: (month) =>
    request('GET', `/api/team?month=${encodeURIComponent(month)}`),
  // Drill-in impersonation — server enforces hierarchy permission, so
  // a non-approver passing ?email=… just gets 403.
  entriesFor: (email, date) =>
    request(
      'GET',
      `/api/entries?email=${encodeURIComponent(email)}&date=${encodeURIComponent(date)}`,
    ),
  entriesRangeFor: (email, from, to) =>
    request(
      'GET',
      `/api/entries?email=${encodeURIComponent(email)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),
  // v2.0 (A7) — approve / reopen.
  approveMonth: (employeeEmail, month) =>
    request('POST', '/api/approvals', { employeeEmail, month }),
  reopenMonth: (employeeEmail, month) =>
    request(
      'DELETE',
      `/api/approvals?employeeEmail=${encodeURIComponent(employeeEmail)}&month=${encodeURIComponent(month)}`,
    ),
  // v2.0 admin — manual trigger for the D+1 notifications cron.
  // Super-approver gated server-side.
  runNotificationsNow: () =>
    request('POST', '/api/admin/run-notifications-now', {}),
  // v2.0 admin — read-only snapshot of today's notification queue across
  // both teams. Super-approver gated server-side.
  notificationsStatus: () =>
    request('GET', '/api/admin/notifications-status'),
  // v2.0 admin — per-day, per-team notification history for the heatmap.
  // Server caps the range at 90 days; we typically pull one month.
  notificationsHistory: (from, to) =>
    request(
      'GET',
      `/api/admin/notifications-history?from=${encodeURIComponent(
        from,
      )}&to=${encodeURIComponent(to)}`,
    ),
  // Reports — org-wide client analytics. Super-approver gated server-side
  // (403 for anyone else). With no month, the server defaults to the last
  // complete month and echoes it back so the picker reflects the selection.
  reportsClientAnalytics: (month, department) => {
    const params = new URLSearchParams();
    if (month) params.set('month', month);
    if (department) params.set('department', department);
    const q = params.toString();
    return request('GET', `/api/reports/client-analytics${q ? '?' + q : ''}`);
  },
  // v2.4 — admin-managed client directory.
  // `listClients` is open to any authed caller (powers both the picker
  // and the admin screen). `addClient` is approver/super-approver only
  // server-side; 403 surfaces back to the dialog as an error toast.
  listClients: (q) => {
    const params = new URLSearchParams({ status: 'active' });
    if (q) params.set('q', q);
    return request('GET', `/api/clients?${params.toString()}`);
  },
  addClient: (name) => request('POST', '/api/clients', { name }),
};
